import { useState } from 'react'

import type { TrendPoint } from '@/lib/api/endpoints/scorecard'
import { cx } from '@/lib/browser/class-names'

export interface UrgencyLevel {
  level: number
  label: string
  text?: string | null | undefined
}

export interface TrendChartProps {
  points: readonly TrendPoint[]
  urgencyLevels?: readonly UrgencyLevel[]
}

const MIN = 1
const MAX = 5

interface Series {
  readonly key: string
  readonly label: string
  readonly color: string
  readonly dashed: boolean
  readonly value: (point: TrendPoint) => number | null | undefined
}

const SERIES: readonly Series[] = [
  {
    key: 'def',
    label: 'Defensibility',
    color: 'var(--color-green-600)',
    dashed: false,
    value: (p) => p.compositeDefensibility,
  },
  {
    key: 'rdy',
    label: 'Readiness',
    color: 'var(--color-green-400)',
    dashed: false,
    value: (p) => p.compositeReadiness,
  },
  {
    key: 'portco',
    label: 'Portco readiness (self)',
    color: 'var(--color-green-400)',
    dashed: true,
    value: (p) => p.portcoReadiness,
  },
  {
    key: 'urgency',
    label: 'Urgency',
    color: 'var(--color-red-400)',
    dashed: false,
    value: (p) => p.urgencyIndex,
  },
]

interface Vertex {
  readonly index: number
  readonly x: number
  readonly y: number
}

function monotonePath(vertices: readonly Vertex[]): string {
  if (vertices.length === 0) return ''
  const [first] = vertices
  if (!first) return ''

  const start = `M${first.x.toFixed(2)},${first.y.toFixed(2)}`
  if (vertices.length === 1) return start

  const slopes: number[] = []
  const tangents: number[] = []

  for (let i = 0; i < vertices.length - 1; i += 1) {
    const a = vertices[i]
    const b = vertices[i + 1]
    if (!a || !b) continue
    slopes.push((b.y - a.y) / (b.x - a.x))
  }

  for (let i = 0; i < vertices.length; i += 1) {
    const before = slopes[i - 1]
    const after = slopes[i]

    if (before === undefined) {
      tangents.push(after ?? 0)
    } else if (after === undefined) {
      tangents.push(before)
    } else if (before * after <= 0) {
      tangents.push(0)
    } else {
      tangents.push((before + after) / 2)
    }
  }

  let path = start

  for (let i = 0; i < vertices.length - 1; i += 1) {
    const a = vertices[i]
    const b = vertices[i + 1]
    const ta = tangents[i]
    const tb = tangents[i + 1]
    if (!a || !b || ta === undefined || tb === undefined) continue

    const third = (b.x - a.x) / 3
    const c1x = a.x + third
    const c1y = a.y + ta * third
    const c2x = b.x - third
    const c2y = b.y - tb * third

    path += ` C${c1x.toFixed(2)},${c1y.toFixed(2)} ${c2x.toFixed(2)},${c2y.toFixed(2)} ${b.x.toFixed(2)},${b.y.toFixed(2)}`
  }

  return path
}

const MIN_TICK_GAP_PCT = 6

function xTicks(ordered: readonly TrendPoint[]): { point: TrendPoint; index: number }[] {
  const position = (index: number) =>
    ordered.length === 1 ? 50 : (index / (ordered.length - 1)) * 100

  const kept: { point: TrendPoint; index: number }[] = []

  ordered.forEach((point, index) => {
    const previous = kept[kept.length - 1]
    if (previous && position(index) - position(previous.index) < MIN_TICK_GAP_PCT) return
    kept.push({ point, index })
  })

  const lastIndex = ordered.length - 1
  if (kept.length > 1 && kept[kept.length - 1]?.index !== lastIndex) {
    const lastPoint = ordered[lastIndex]
    const previous = kept[kept.length - 1]
    if (lastPoint && previous) {
      if (position(lastIndex) - position(previous.index) < MIN_TICK_GAP_PCT) kept.pop()
      kept.push({ point: lastPoint, index: lastIndex })
    }
  }

  return kept
}

export function TrendChart({ points, urgencyLevels = [] }: TrendChartProps) {
  const [active, setActive] = useState<number | null>(null)

  if (points.length === 0) {
    return <div className="canopy-empty-state">No score history yet.</div>
  }

  const ordered = [...points].sort((a, b) => a.sortKey.localeCompare(b.sortKey))

  const x = (index: number) => (ordered.length === 1 ? 50 : (index / (ordered.length - 1)) * 100)
  const y = (value: number) => ((MAX - Math.min(Math.max(value, MIN), MAX)) / (MAX - MIN)) * 100

  const drawn = SERIES.map((series) => {
    const runs: Vertex[][] = []
    let run: Vertex[] = []

    ordered.forEach((point, index) => {
      const value = series.value(point)
      if (value === null || value === undefined) {
        if (run.length > 0) runs.push(run)
        run = []
        return
      }
      run.push({ index, x: x(index), y: y(value) })
    })
    if (run.length > 0) runs.push(run)

    return { series, runs, vertices: runs.flat() }
  }).filter((entry) => entry.vertices.length > 0)

  const ticks = xTicks(ordered)

  return (
    <div>
      <div className="canopy-trend-wrap">
        <div className="canopy-trend-axis-title">Score 1–5</div>

        <div className="canopy-trend-plot">
          {[5, 4, 3, 2, 1].map((value) => (
            <div key={value} className="canopy-trend-grid" style={{ top: `${String(y(value))}%` }}>
              <span className="canopy-trend-ytick">{value}</span>
            </div>
          ))}

          <svg
            className="canopy-trend-svg"
            viewBox="0 0 100 100"
            preserveAspectRatio="none"
            role="img"
            aria-label={`Score trend across ${String(ordered.length)} snapshots`}
          >
            {drawn.map(({ series, runs }) =>
              runs
                .filter((run) => run.length > 1)
                .map((run, i) => (
                  <path
                    key={`${series.key}-${String(i)}`}
                    data-series={series.key}
                    d={monotonePath(run)}
                    fill="none"
                    stroke={series.color}
                    strokeWidth={2}
                    strokeLinecap="round"
                    vectorEffect="non-scaling-stroke"
                    {...(series.dashed ? { strokeDasharray: '4 4' } : {})}
                  />
                )),
            )}
          </svg>

          {drawn.map(({ series, vertices }) =>
            vertices.map((vertex) => (
              <div
                key={`${series.key}-${String(vertex.index)}`}
                className={cx('canopy-trend-dot', active === vertex.index && 'is-active')}
                style={{
                  left: `${String(vertex.x)}%`,
                  top: `${String(vertex.y)}%`,
                  borderColor: series.color,
                }}
              />
            )),
          )}

          <div className="canopy-trend-hover">
            {ordered.map((point, index) => (
              <div
                key={point.key}
                className="canopy-trend-hover-band"
                onMouseEnter={() => {
                  setActive(index)
                }}
                onFocus={() => {
                  setActive(index)
                }}
                onMouseLeave={() => {
                  setActive(null)
                }}
                onBlur={() => {
                  setActive(null)
                }}
                tabIndex={0}
                role="button"
                aria-label={`Snapshot ${point.label}`}
              />
            ))}
          </div>

          {active !== null && ordered[active] && (
            <TrendTooltip point={ordered[active]} urgencyLevels={urgencyLevels} left={x(active)} />
          )}
        </div>

        <div className="canopy-trend-xaxis">
          {ticks.map(({ point, index }) => (
            <span
              key={point.key}
              className="canopy-trend-xtick"
              style={{ left: `${String(x(index))}%` }}
            >
              {point.label}
            </span>
          ))}
        </div>
      </div>

      <div className="canopy-trend-legend">
        {drawn.map(({ series }) => (
          <div key={series.key} className="canopy-trend-leg-item">
            <span
              className={cx('canopy-trend-leg-line', series.dashed && 'is-dashed')}
              style={{ color: series.color }}
            />
            {series.label}
          </div>
        ))}
      </div>

      <p className="canopy-trend-note">
        Defensibility &amp; Readiness: 1 = exposed/early, 5 = well-defended/mature (higher is
        better). Urgency: 1 = Low, 5 = Critical (higher is worse).
      </p>
    </div>
  )
}

function urgencyLabelFor(value: number, levels: readonly UrgencyLevel[]): string | null {
  if (levels.length === 0) return null

  const nearest = [...levels].sort(
    (a, b) => Math.abs(a.level - value) - Math.abs(b.level - value),
  )[0]

  return nearest?.label ?? null
}

function TrendTooltip({
  point,
  urgencyLevels,
  left,
}: {
  point: TrendPoint
  urgencyLevels: readonly UrgencyLevel[]
  left: number
}) {
  const rows = SERIES.map((series) => ({ series, value: series.value(point) })).filter(
    (row): row is { series: Series; value: number } =>
      row.value !== null && row.value !== undefined,
  )

  return (
    <div
      className="canopy-trend-tip"
      style={{ left: `${String(left)}%`, transform: left > 60 ? 'translateX(-100%)' : undefined }}
    >
      <div className="canopy-trend-tip-date">{point.label}</div>
      {rows.map(({ series, value }) => (
        <div key={series.key} className="canopy-trend-tip-row">
          <span className="canopy-trend-tip-label">{series.label}</span>
          <span className="canopy-trend-tip-val">
            {value.toFixed(2)}
            {series.key === 'urgency' && urgencyLabelFor(value, urgencyLevels) !== null && (
              <span className="canopy-trend-tip-note">
                {' · '}
                {urgencyLabelFor(value, urgencyLevels)}
              </span>
            )}
          </span>
        </div>
      ))}
      {(point.serentSource || point.portcoSource) && (
        <div className="canopy-trend-tip-chips">
          {point.serentSource && <span className="canopy-trend-chip is-serent">serent</span>}
          {point.portcoSource && <span className="canopy-trend-chip is-portco">portco</span>}
        </div>
      )}
    </div>
  )
}
